/**
 * Shared merge for App Dashboard / App Menu update wizards.
 * Requirement: show tenant app_dashboard/app_menu JSON + master catalog,
 * tenant wins on same action, no duplicate actions.
 */
import { resolveIconWithFallback } from "./appItemIcon";

export const normalizeActionKey = (action) =>
  String(action || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const toListItem = (item, { checked, tenantOnly, masterItem }) => {
  const icons = resolveIconWithFallback(item.icon, masterItem?.icon || item.icon);
  return {
    ...(masterItem || {}),
    ...item,
    ...icons,
    icon: icons.primaryIcon,
    name: item.name ?? masterItem?.name,
    action: item.action ?? masterItem?.action,
    link: item.link ?? masterItem?.link,
    order_at: item.order_at ?? masterItem?.order_at,
    order: item.order_at ?? item.order ?? masterItem?.order_at,
    id: item.id ?? masterItem?.id,
    checked,
    tenantOnly,
    companyOverride: Boolean(masterItem && !tenantOnly),
  };
};

/**
 * Merge tenant-saved items with master catalog.
 * Tenant JSON is listed first (checked); master fills gaps (unchecked).
 * Duplicate actions: tenant row wins.
 */
export const mergeAppItemsByAction = (masterItems = [], tenantItems = []) => {
  const masterByAction = new Map();
  (masterItems || []).forEach((item) => {
    const key = normalizeActionKey(item.action);
    if (key) masterByAction.set(key, item);
  });

  const merged = [];
  const seenActions = new Set();

  (tenantItems || []).forEach((tenantItem) => {
    const actionKey = normalizeActionKey(tenantItem.action);
    if (!actionKey) return;

    const masterItem = masterByAction.get(actionKey);
    merged.push(
      toListItem(tenantItem, {
        checked: true,
        tenantOnly: !masterItem,
        masterItem,
      }),
    );
    seenActions.add(actionKey);
  });

  (masterItems || []).forEach((masterItem) => {
    const actionKey = normalizeActionKey(masterItem.action);
    if (!actionKey || seenActions.has(actionKey)) return;

    merged.push(
      toListItem(masterItem, {
        checked: false,
        tenantOnly: false,
        masterItem: null,
      }),
    );
    seenActions.add(actionKey);
  });

  return merged;
};

export const addCheckedKeyToItems = (array = []) =>
  array.map((item) => {
    const icons = resolveIconWithFallback(item.icon, item.icon);
    return {
      ...item,
      ...icons,
      icon: icons.primaryIcon,
      checked: false,
      order: item.order_at ?? item.order,
    };
  });

export const nextCustomItemId = (items = []) => {
  const maxId = items.reduce(
    (max, item) => Math.max(max, Number(item.id) || 0),
    0,
  );
  return maxId + 1;
};
